import { OWNER_STEPS } from '../../../../@types/Owner';
import { JWTAuthorization } from '../../../../utils/JWTHeader';

// API response type
export interface PaginationResponse {
  totalResults: number;
  totalPages: number;
  itemsPerPage: number;
  currentPage: number;
  previousPage: number | null;
  nextPage: number | null;
}

export interface ApiResponse {
  pagination: PaginationResponse;
  results: any[];
}

/**
 * Fetches owners data based on the specified step
 * @param companyId - The ID of the company
 * @param step - The step to filter owners by (from OWNER_STEPS)
 * @param jwt - The JWT token for authentication
 * @param page - The page number to fetch (1-based indexing)
 * @param limit - The number of items per page
 * @param sortField - Optional field to sort results by
 * @returns Promise with the API response containing pagination info and results
 */
export const fetchOwnersByStep = async(
  companyId: number,
  step: string,
  jwt: string,
  page: number = 1,
  limit: number = 10,
  sortField?: string,
  extraFilters?: Record<string, string>,
): Promise<ApiResponse> => {
  try {
    let url = `/api/companies/${companyId}/owners?filterStep=${encodeURIComponent(step)}&step=${page}&limit=${limit}`;
    if (sortField) {
      url += `&sortField=${sortField}`;
    }
    if (extraFilters) {
      for (const [key, value] of Object.entries(extraFilters)) {
        url += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
    }
    
    const response = await fetch(
      url,
      {
        headers: {
          'Authorization': JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch owners with step ${step}`);
    }

    const data = await response.json();
    return {
      pagination: {
        totalResults: data.pagination.totalResults,
        totalPages: data.pagination.totalPages,
        itemsPerPage: limit,
        currentPage: page,
        previousPage: page > 1 ? page - 1 : null,
        nextPage: page < data.pagination.totalPages ? page + 1 : null,
      },
      results: data.results,
    };
  } catch (error) {
    console.error(`Error fetching owners with step ${step}:`, error);
    throw error;
  }
};

/**
 * Map of step names to their corresponding API endpoints
 */
export const STEP_API_MAPPING = {
  [OWNER_STEPS.NOT_CONTACTED]: fetchOwnersByStep,
  [OWNER_STEPS.WAITING_FOR_FEEDBACK]: fetchOwnersByStep,
  [OWNER_STEPS.TO_RECALL]: fetchOwnersByStep,
  [OWNER_STEPS.TO_CALL]: fetchOwnersByStep,
  [OWNER_STEPS.REFUSED]: fetchOwnersByStep,
  [OWNER_STEPS.REFUSED_RENT_MANAGER]: fetchOwnersByStep,
  [OWNER_STEPS.REFUSED_SALES_MANAGER]: fetchOwnersByStep,
  [OWNER_STEPS.UNABLE_TO_REACH]: fetchOwnersByStep,
  [OWNER_STEPS.UNABLE_TO_REACH_RENT_MANAGER]: fetchOwnersByStep,
  [OWNER_STEPS.UNABLE_TO_REACH_SALES_MANAGER]: fetchOwnersByStep,
  [OWNER_STEPS.TO_CONTACT_RENT_MANAGER]: fetchOwnersByStep,
  [OWNER_STEPS.TO_CONTACT_SALES_MANAGER]: fetchOwnersByStep,
  [OWNER_STEPS.WAITING_FOR_MEETING_RENT_MANAGER]: fetchOwnersByStep,
  [OWNER_STEPS.WAITING_FOR_MEETING_SALES_MANAGER]: fetchOwnersByStep,
  [OWNER_STEPS.CLOSED_RENT_MANAGER]: fetchOwnersByStep,
  [OWNER_STEPS.CLOSED_SALES_MANAGER]: fetchOwnersByStep,
}; 